import type { MarketingLocale } from '~/lib/marketing/site';

/**
 * Marketing FAQ corpus: bilingual, query-shaped (each question mirrors a
 * real search query). Shared by the /faq
 * page (full list + FAQPage JSON-LD) and the landing page teaser (first few
 * of `featured`). Grouped by topic for the /faq page layout.
 *
 * Content-file pattern (like whats-new/changelog.json): typed en/ar objects,
 * no locale-file reserialization risk.
 */

/** /faq page metadata; `ar` is served on /ar/faq. */
export const FAQ_PAGE_META = {
  title: 'FAQ: Booking Software Pricing, Payments & WhatsApp | AskFlow',
  description:
    'Answers to what venue owners ask about AskFlow: pricing, local payments, e-invoicing, WhatsApp AI, custom features, onboarding and switching systems.',
  ar: {
    title: 'الأسئلة الشائعة عن نظام الحجز والمدفوعات وواتساب | AskFlow',
    description:
      'إجابات عن أكثر ما يسأله ملّاك المنشآت عن AskFlow: الأسعار ووسائل الدفع المحلية والفوترة الإلكترونية وواتساب الذكي والمزايا المخصصة والانتقال من نظام آخر.',
  },
  path: '/faq',
} as const;

export interface FaqEntry {
  id: string;
  question: { en: string; ar: string };
  answer: { en: string; ar: string };
  /** Featured entries surface on the landing page teaser. */
  featured?: boolean;
}

export interface FaqGroup {
  id: string;
  heading: { en: string; ar: string };
  entries: FaqEntry[];
}

export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: 'getting-started',
    heading: { en: 'Getting started', ar: 'البداية مع المنصة' },
    entries: [
      {
        id: 'what-is-flow',
        featured: true,
        question: {
          en: 'What is Flow and who is it for?',
          ar: 'ما هي منصة Flow ولمن صُممت؟',
        },
        answer: {
          en: 'Flow (AskFlow) is a booking and operations platform for padel and tennis clubs, yoga and pilates studios, clinics and wellness centres, gyms, membership clubs, sports academies, and schools or multi-site operators. It combines online booking, payments, packages and memberships, a WhatsApp AI concierge, a POS and a CRM in one system under your own brand, and we keep building the features you ask for.',
          ar: 'Flow (AskFlow) منصة حجوزات وتشغيل لأندية البادل والتنس، واستوديوهات اليوغا والبيلاتس، والعيادات ومراكز العافية، والصالات الرياضية، وأندية العضويات، والأكاديميات الرياضية، والمدارس والمشغلين متعددي المواقع. تجمع الحجز الإلكتروني والمدفوعات والباقات والعضويات ومساعدًا ذكيًا على واتساب ونقاط بيع وإدارة للعملاء في نظام واحد يحمل علامتك، ونواصل بناء المزايا التي تطلبها.',
        },
      },
      {
        id: 'how-fast-launch',
        featured: true,
        question: {
          en: 'How fast can my venue go live on Flow?',
          ar: 'كم يستغرق إطلاق منشأتي على Flow؟',
        },
        answer: {
          en: 'Most venues launch within days. We set up your branded booking page, courts or class schedules, pricing and payment account with you, then import your existing customers and package balances from Excel or your previous system, so you never start from zero.',
          ar: 'معظم المنشآت تنطلق خلال أيام معدودة. نجهّز معك صفحة الحجز بعلامتك التجارية، والملاعب أو جداول الحصص، والأسعار وحساب المدفوعات، ثم نستورد عملاءك الحاليين وأرصدة باقاتهم من ملفات Excel أو نظامك السابق، فلا تبدأ من الصفر أبدًا.',
        },
      },
      {
        id: 'switch-from-competitor',
        question: {
          en: 'Can I switch to Flow from Mindbody, Glofox or spreadsheets?',
          ar: 'هل يمكنني الانتقال إلى Flow من Mindbody أو Glofox أو من جداول Excel؟',
        },
        answer: {
          en: 'Yes. Flow has a self-serve customer and package-balance import, and our team handles the migration with you: customers, memberships, remaining package credits and upcoming bookings. Venues typically run their first real booking day on Flow within a week of deciding to switch.',
          ar: 'نعم. توفر Flow استيرادًا ذاتيًا للعملاء وأرصدة الباقات، ويرافقك فريقنا في نقل البيانات: العملاء والعضويات وأرصدة الباقات المتبقية والحجوزات القادمة. عادةً ما تدير المنشآت أول يوم حجوزات فعلي على Flow خلال أسبوع من قرار الانتقال.',
        },
      },
      {
        id: 'own-brand',
        featured: true,
        question: {
          en: 'Will the booking page carry my brand or yours?',
          ar: 'هل تحمل صفحة الحجز علامتي التجارية أم علامتكم؟',
        },
        answer: {
          en: 'Yours, 100%. Your logo, your colors, your own domain. Customers book at your own website address, and there is no marketplace exposing your members to competing venues. Tenants can even ship their own branded iOS and Android app.',
          ar: 'علامتك أنت بنسبة 100%. شعارك وألوانك ونطاقك الخاص. يحجز عملاؤك عبر موقعك أنت، ولا يوجد سوق مشترك يعرض أعضاءك على منشآت منافسة. ويمكن للمنشآت إطلاق تطبيق iOS وAndroid خاص بها أيضًا.',
        },
      },
      {
        id: 'yoga-pilates-studios',
        question: {
          en: 'Do you work with yoga and pilates studios?',
          ar: 'هل تعملون مع استوديوهات اليوغا والبيلاتس؟',
        },
        answer: {
          en: 'Yes. Studios run class schedules, credit packs with expiry windows, memberships and instructor calendars on Flow. When a full class frees up, the waitlist promotes the next client automatically, and members book from an app that carries your studio name.',
          ar: 'نعم. تدير الاستوديوهات على Flow جداول الحصص وباقات الأرصدة بصلاحية محددة والعضويات وتقاويم المدربين. وعندما يتوفر مكان في حصة ممتلئة، تنقل قائمة الانتظار العميلة التالية تلقائيًا، ويحجز الأعضاء من تطبيق يحمل اسم استوديوك.',
        },
      },
      {
        id: 'clinics-wellness',
        featured: true,
        question: {
          en: 'Does Flow work for clinics and wellness centres?',
          ar: 'هل تناسب Flow العيادات ومراكز العافية؟',
        },
        answer: {
          en: 'Yes. Physiotherapy, chiropractic, therapy, recovery and wellness clinics run appointments on Flow: each practitioner has their own calendar and services, clients buy courses or session packages, intake questions are collected at sign-up, and confirmations and reminders go out on WhatsApp. Flow runs scheduling, payments and client records; it is not a clinical notes or medical records system.',
          ar: 'نعم. تدير عيادات العلاج الطبيعي وتقويم العمود الفقري والعلاج النفسي والتعافي ومراكز العافية مواعيدها على Flow: لكل أخصائي تقويمه وخدماته، ويشتري العملاء دورات أو باقات جلسات، وتُجمع أسئلة التسجيل عند إنشاء الحساب، وتُرسل التأكيدات والتذكيرات عبر واتساب. تدير Flow المواعيد والمدفوعات وسجلات العملاء، لكنها ليست نظامًا للملاحظات السريرية أو السجلات الطبية.',
        },
      },
      {
        id: 'membership-clubs',
        question: {
          en: 'Does Flow work for membership clubs?',
          ar: 'هل تناسب Flow أندية العضويات؟',
        },
        answer: {
          en: 'Yes. Clubs run tiered and family memberships with member pricing and booking windows, digital membership cards with check-in at the door, guest passes members send from the app, events, and renewal reminders on WhatsApp with a payment link. Members renew in one tap; Flow does not charge saved cards automatically.',
          ar: 'نعم. تدير الأندية على Flow عضويات بفئات وعضويات عائلية بأسعار ونوافذ حجز خاصة، وبطاقات عضوية رقمية مع تسجيل الدخول عند البوابة، وتصاريح ضيوف يرسلها الأعضاء من التطبيق، وفعاليات، وتذكيرات تجديد عبر واتساب برابط دفع. يجدد العضو بلمسة واحدة، ولا تخصم Flow من بطاقات محفوظة تلقائيًا.',
        },
      },
      {
        id: 'schools-organizations',
        question: {
          en: 'Can schools and companies book sessions through Flow?',
          ar: 'هل تستطيع المدارس والشركات حجز الحصص عبر Flow؟',
        },
        answer: {
          en: 'Yes. You can set up a recurring contract series of court and coach sessions for a school or company, send a quote, and invoice the organization once it is accepted. Parents use family accounts to enroll and pay for their children, and multi-site teams get roles scoped by location.',
          ar: 'نعم. يمكنك إنشاء سلسلة عقد متكررة من حصص الملاعب والمدربين لمدرسة أو شركة، وإرسال عرض السعر، وإصدار الفاتورة باسم الجهة بعد قبوله. ويستخدم أولياء الأمور الحسابات العائلية لتسجيل أبنائهم والدفع عنهم، وتحصل الفرق متعددة المواقع على صلاحيات حسب الموقع.',
        },
      },
    ],
  },
  {
    id: 'custom-features',
    heading: { en: 'Custom features', ar: 'المزايا المخصصة' },
    entries: [
      {
        id: 'build-what-we-need',
        featured: true,
        question: {
          en: 'Can you build a feature my venue needs?',
          ar: 'هل يمكنكم بناء ميزة تحتاجها منشأتي؟',
        },
        answer: {
          en: 'Yes. Requests go straight to the team that builds Flow. We review them with you, and many of the features clubs and studios use every day started as one venue\u2019s request. If it helps how you run your business, we will find a way to build it.',
          ar: 'نعم. تصل طلباتك مباشرة إلى الفريق الذي يبني Flow، فنراجعها معك، وكثير من المزايا التي تستخدمها الأندية والاستوديوهات يوميًا بدأت بطلب من منشأة واحدة. وإن كانت الميزة تخدم طريقة إدارتك لعملك، سنجد طريقة لبنائها.',
        },
      },
      {
        id: 'release-pace',
        question: {
          en: 'How often do you ship new features?',
          ar: 'كم مرة تطلقون مزايا جديدة؟',
        },
        answer: {
          en: 'Every week. We shipped more than 600 features in the last three months, many of them first built for a single client and then made available to every venue on Flow.',
          ar: 'كل أسبوع. أطلقنا أكثر من 600 ميزة خلال الأشهر الثلاثة الأخيرة، كثير منها بُني أولًا لعميل واحد ثم أصبح متاحًا لكل المنشآت على Flow.',
        },
      },
    ],
  },
  {
    id: 'payments',
    heading: { en: 'Payments & invoicing', ar: 'المدفوعات والفوترة' },
    entries: [
      {
        id: 'payment-methods',
        featured: true,
        question: {
          en: 'Which payment methods can my customers use?',
          ar: 'ما وسائل الدفع المتاحة لعملائي؟',
        },
        answer: {
          en: 'Visa, Mastercard and Apple Pay, plus local methods such as mada, through licensed payment gateways including Stripe, with payouts in your currency to your business bank account. Cash, bank transfer and pay-at-venue flows are supported too, including split payments.',
          ar: 'فيزا وماستركارد وApple Pay، إضافة إلى وسائل محلية مثل مدى، عبر بوابات دفع مرخصة منها Stripe، مع تحويل المستحقات بعملتك إلى حسابك البنكي التجاري. كما تدعم الدفع النقدي والتحويل البنكي والدفع في المنشأة، بما في ذلك تقسيم المدفوعات.',
        },
      },
      {
        id: 'zatca',
        question: {
          en: 'Is Flow compliant with ZATCA e-invoicing?',
          ar: 'هل تتوافق Flow مع الفوترة الإلكترونية لهيئة الزكاة والضريبة والجمارك؟',
        },
        answer: {
          en: 'Yes. Flow issues ZATCA-compliant e-invoices automatically, either through your accounting system (Oracle, Odoo, Zoho Books, Wafeq, Qoyod or Daftra) or on its own through eInvoiceMe, so every paid booking, package and membership gets a compliant invoice without manual work.',
          ar: 'نعم. تُصدر Flow فواتير إلكترونية متوافقة مع متطلبات هيئة الزكاة والضريبة والجمارك تلقائيًا، إما عبر نظامك المحاسبي (Oracle أو Odoo أو Zoho Books أو Wafeq أو Qoyod أو Daftra) أو وحدها عبر eInvoiceMe، فكل حجز وباقة وعضوية مدفوعة تحصل على فاتورة متوافقة دون أي عمل يدوي.',
        },
      },
      {
        id: 'accounting-integrations',
        featured: true,
        question: {
          en: 'Which accounting systems does Flow connect to?',
          ar: 'ما الأنظمة المحاسبية التي تتصل بها Flow؟',
        },
        answer: {
          en: 'Oracle, Odoo and Zoho Books, plus Wafeq, Qoyod and Daftra. Invoices and credit notes sync automatically, including ZATCA-compliant e-invoices for businesses in Saudi Arabia, and venues that only need ZATCA e-invoicing can use eInvoiceMe on its own. If you use another system, tell us: integrations are part of what we build on request.',
          ar: 'Oracle وOdoo وZoho Books، إضافة إلى Wafeq وQoyod وDaftra. تتزامن الفواتير وإشعارات الدائن تلقائيًا، بما فيها الفواتير الإلكترونية المتوافقة مع هيئة الزكاة والضريبة والجمارك للمنشآت في السعودية، ويمكن للمنشآت التي تحتاج فوترة زاتكا فقط استخدام eInvoiceMe وحدها. وإن كنت تستخدم نظامًا آخر فأخبرنا، فالتكاملات جزء مما نبنيه عند الطلب.',
        },
      },
      {
        id: 'refunds-credit',
        question: {
          en: 'How do refunds and customer credit work?',
          ar: 'كيف تعمل الاستردادات ورصيد العملاء؟',
        },
        answer: {
          en: 'You choose per case: refund to the original payment method or issue venue credit the customer can spend on their next booking. Cancellation windows, no-show policies and credit validity are all configurable per venue.',
          ar: 'الخيار لك في كل حالة: استرداد إلى وسيلة الدفع الأصلية أو إصدار رصيد لدى المنشأة يستخدمه العميل في حجزه القادم. نوافذ الإلغاء وسياسات عدم الحضور وصلاحية الرصيد كلها قابلة للتخصيص لكل منشأة.',
        },
      },
    ],
  },
  {
    id: 'whatsapp-ai',
    heading: { en: 'WhatsApp & AI', ar: 'واتساب والذكاء الاصطناعي' },
    entries: [
      {
        id: 'whatsapp-notifications',
        featured: true,
        question: {
          en: 'Does Flow send booking confirmations and reminders on WhatsApp?',
          ar: 'هل ترسل Flow تأكيدات الحجز والتذكيرات عبر واتساب؟',
        },
        answer: {
          en: 'Yes, natively, through the official WhatsApp Business API. Confirmations, reminders, cancellations and package-renewal notices go out in Arabic or English automatically. Over 200,000 WhatsApp messages have been delivered through the platform.',
          ar: 'نعم، وبشكل مدمج عبر واجهة واتساب للأعمال الرسمية. تُرسل التأكيدات والتذكيرات والإلغاءات وإشعارات تجديد الباقات تلقائيًا بالعربية أو الإنجليزية. وقد تم إرسال أكثر من 200,000 رسالة واتساب عبر المنصة.',
        },
      },
      {
        id: 'ai-assistant',
        question: {
          en: 'What does the AI assistant actually do?',
          ar: 'ما الذي يقوم به المساعد الذكي فعليًا؟',
        },
        answer: {
          en: 'Two assistants: a customer-facing one that answers questions and takes bookings over WhatsApp in Arabic and English, and an admin assistant that answers questions about your business, from occupancy and revenue to your top customers, and helps your team operate the dashboard.',
          ar: 'مساعدان: الأول للعملاء يجيب عن الأسئلة ويستقبل الحجوزات عبر واتساب بالعربية والإنجليزية، والثاني للإدارة يجيب عن أسئلتك حول أعمالك، من الإشغال والإيرادات إلى أفضل عملائك، ويساعد فريقك في استخدام لوحة التحكم.',
        },
      },
    ],
  },
  {
    id: 'operations',
    heading: { en: 'Operations', ar: 'التشغيل' },
    entries: [
      {
        id: 'multi-location',
        question: {
          en: 'Can Flow manage multiple branches and locations?',
          ar: 'هل تدير Flow عدة فروع ومواقع؟',
        },
        answer: {
          en: 'Yes. Multi-location venues run on one dashboard with per-branch schedules, pricing, staff permissions and reports. Staff access can be scoped to a single branch or a region, and owners see everything.',
          ar: 'نعم. تعمل المنشآت متعددة الفروع من لوحة تحكم واحدة مع جداول وأسعار وصلاحيات موظفين وتقارير لكل فرع. ويمكن تحديد وصول الموظفين لفرع واحد أو منطقة، بينما يرى المالك كل شيء.',
        },
      },
      {
        id: 'packages-memberships',
        question: {
          en: 'Does Flow support class packages, memberships and coaching programs?',
          ar: 'هل تدعم Flow باقات الحصص والعضويات وبرامج التدريب؟',
        },
        answer: {
          en: 'All three. Sell session packages with expiry and redemption windows, recurring memberships with member-only pricing and access, group classes with waitlists, and private coaching with per-coach schedules and pay tracking.',
          ar: 'الثلاثة معًا. بِع باقات حصص بصلاحية ونوافذ استخدام محددة، وعضويات متجددة بأسعار ومزايا خاصة بالأعضاء، وحصصًا جماعية بقوائم انتظار، وتدريبًا خاصًا بجداول لكل مدرب ومتابعة مستحقاته.',
        },
      },
      {
        id: 'reports',
        question: {
          en: 'What reports and analytics do I get?',
          ar: 'ما التقارير والتحليلات المتاحة لي؟',
        },
        answer: {
          en: 'Daily sales, occupancy and peak-hour utilization, revenue by service and location, coach performance, customer insights and exportable booking history, plus an AI assistant you can simply ask.',
          ar: 'المبيعات اليومية، الإشغال واستغلال ساعات الذروة، الإيرادات حسب الخدمة والموقع، أداء المدربين، تحليلات العملاء وسجل حجوزات قابل للتصدير، إضافةً إلى مساعد ذكي يمكنك سؤاله مباشرة.',
        },
      },
    ],
  },
  {
    id: 'pricing',
    heading: { en: 'Pricing & plans', ar: 'السعر والباقات' },
    entries: [
      {
        id: 'how-much-cost',
        question: {
          en: 'How much does Flow booking software cost?',
          ar: 'كم تكلفة منصة الحجوزات Flow؟',
        },
        answer: {
          en: 'Pricing is transparent and billed in your local currency, with no hidden fees. What you pay depends on your venue size and number of locations. Contact us and we will put together a quote tailored to your venue rather than forcing you into a one-size-fits-all tier.',
          ar: 'أسعارنا واضحة وتُحتسب بعملتك المحلية، وبدون أي رسوم مخفية. وتعتمد التكلفة على حجم منشأتك وعدد فروعك. تواصل معنا وسنجهّز لك عرض سعر مفصّلًا على مقاس منشأتك، بدل باقة موحّدة لا تناسب الجميع.',
        },
      },
      {
        id: 'long-term-contract',
        question: {
          en: 'Is there a long-term contract or lock-in?',
          ar: 'هل يوجد عقد التزام طويل أو تقييد؟',
        },
        answer: {
          en: 'No. There is no long-term lock-in and no surprise price hikes at renewal. We would rather keep you by being the system your team actually likes using than by a contract clause.',
          ar: 'لا. لا يوجد التزام طويل المدى ولا زيادات مفاجئة في السعر عند التجديد. نفضّل أن نكسب بقاءك لأن فريقك يحب استخدام النظام فعلًا، لا بسبب بند في العقد.',
        },
      },
      {
        id: 'demo-trial',
        question: {
          en: 'Is there a demo or trial of Flow?',
          ar: 'هل يوجد عرض توضيحي أو تجربة لمنصة Flow؟',
        },
        answer: {
          en: 'Yes. The best way to evaluate Flow is a live demo on your own venue setup. Schedule a demo and we will walk you through booking, payments, packages and the dashboard using scenarios from your actual business.',
          ar: 'نعم. أفضل طريقة لتقييم Flow هي عرض مباشر على إعدادات منشأتك. احجز عرضًا توضيحيًا وسنستعرض معك الحجوزات والمدفوعات والباقات ولوحة التحكم بسيناريوهات من عملك الفعلي.',
        },
      },
    ],
  },
  {
    id: 'support-onboarding',
    heading: { en: 'Support & onboarding', ar: 'الدعم والتهيئة' },
    entries: [
      {
        id: 'what-support',
        question: {
          en: 'What support do I get with Flow?',
          ar: 'ما نوع الدعم الذي أحصل عليه مع Flow؟',
        },
        answer: {
          en: 'Real people in the region answer you on WhatsApp, in Arabic and English, 24/7. No offshore ticket queues: you message the same team that set up your venue, and they know your setup.',
          ar: 'يرد عليك أشخاص حقيقيون من داخل المنطقة عبر واتساب، بالعربية والإنجليزية، على مدار الساعة. لا طوابير تذاكر خارجية: تراسل نفس الفريق الذي جهّز منشأتك وهو يعرف تفاصيل إعداداتك.',
        },
      },
      {
        id: 'who-sets-up',
        question: {
          en: 'Who sets up the system, and do I have to configure everything myself?',
          ar: 'من يقوم بإعداد النظام، وهل عليّ تجهيز كل شيء بنفسي؟',
        },
        answer: {
          en: 'We do the setup with you. Our team configures your courts and schedules, pricing and peak-hour rates, packages and memberships, payment account and WhatsApp alongside you, so you open on day one with a working system.',
          ar: 'نقوم بالإعداد معك. يجهّز فريقنا بجانبك الملاعب والجداول، والأسعار وأسعار ساعات الذروة، والباقات والعضويات، وحساب المدفوعات وواتساب، لتبدأ يومك الأول بنظام جاهز للعمل.',
        },
      },
      {
        id: 'staff-training',
        question: {
          en: 'Do you train my staff on the system?',
          ar: 'هل تدرّبون موظفيّ على النظام؟',
        },
        answer: {
          en: 'Yes. We train your front desk, coaches and managers on the flows they actually use: taking bookings, checking members in, handling payments and refunds. We also stay available on WhatsApp when new staff join.',
          ar: 'نعم. ندرّب موظفي الاستقبال والمدربين والمدراء على المهام التي يستخدمونها فعلًا: استقبال الحجوزات، وتسجيل دخول الأعضاء، والمدفوعات والاستردادات. ونبقى متاحين عبر واتساب عند انضمام موظفين جدد.',
        },
      },
    ],
  },
  {
    id: 'product-depth',
    heading: { en: 'Product depth', ar: 'إمكانات المنصة' },
    entries: [
      {
        id: 'pos',
        question: {
          en: 'Does Flow have a POS for retail and cafe sales?',
          ar: 'هل توفر Flow نقاط بيع للمتجر والمقهى؟',
        },
        answer: {
          en: 'Yes. Flow includes a built-in POS for selling retail items, cafe orders and add-ons at the venue, with sales flowing into the same daily reports and ZATCA-compliant invoicing as your bookings.',
          ar: 'نعم. تتضمن Flow نقاط بيع مدمجة لبيع منتجات المتجر وطلبات المقهى والإضافات داخل المنشأة، وتنساب المبيعات إلى نفس التقارير اليومية والفوترة المتوافقة مع هيئة الزكاة والضريبة والجمارك مثل حجوزاتك تمامًا.',
        },
      },
      {
        id: 'split-payments',
        question: {
          en: 'Can customers split the payment for a booking?',
          ar: 'هل يمكن للعملاء تقسيم دفع الحجز بينهم؟',
        },
        answer: {
          en: 'Yes. Split payments let players share the cost of a court or session. Each pays their own share by mada, Apple Pay or card, and the organizer is no longer stuck collecting cash from friends.',
          ar: 'نعم. تقسيم المدفوعات يتيح للاعبين مشاركة تكلفة الملعب أو الجلسة، فكلٌّ يدفع حصته عبر مدى أو Apple Pay أو البطاقة، ولا يضطر منظّم الحجز لملاحقة أصدقائه لتحصيل المبلغ.',
        },
      },
      {
        id: 'coaches-academies',
        question: {
          en: 'Does Flow support coaches and academies?',
          ar: 'هل تدعم Flow المدربين والأكاديميات؟',
        },
        answer: {
          en: 'Yes. Coaching is a first-class part of Flow: per-coach schedules and availability, private sessions, structured academy programs, and coach pay tracking so you always know what each coach has earned.',
          ar: 'نعم. التدريب جزء أساسي من Flow: جداول وتوفّر لكل مدرب، جلسات خاصة، برامج أكاديمية منظمة، ومتابعة مستحقات المدربين لتعرف دائمًا ما كسبه كل مدرب.',
        },
      },
      {
        id: 'mobile-app',
        question: {
          en: 'Is there a mobile app for my customers?',
          ar: 'هل يوجد تطبيق جوال لعملائي؟',
        },
        answer: {
          en: 'Yes. Venues on Flow can ship their own branded iOS and Android app: your name and icon on the App Store and Google Play, with booking, packages, memberships and payments inside. Your web booking page works great on mobile from day one either way.',
          ar: 'نعم. يمكن للمنشآت على Flow إطلاق تطبيق iOS وAndroid خاص بها: اسمك وأيقونتك على App Store وGoogle Play، وبداخله الحجز والباقات والعضويات والمدفوعات. وصفحة الحجز عبر المتصفح تعمل جيدًا على الجوال من اليوم الأول في كل الأحوال.',
        },
      },
      {
        id: 'discounts-promo-codes',
        question: {
          en: 'Can I run discounts and promo codes on Flow?',
          ar: 'هل يمكنني إطلاق خصومات وأكواد خصم على Flow؟',
        },
        answer: {
          en: 'Yes. Create discount and promo codes for bookings and packages, including public offers you can share in campaigns. Combine them with peak and off-peak pricing to fill quiet hours.',
          ar: 'نعم. أنشئ خصومات وأكواد خصم للحجوزات والباقات، بما فيها عروض عامة تشاركها في حملاتك. وادمجها مع تسعير ساعات الذروة وخارجها لملء الأوقات الهادئة.',
        },
      },
      {
        id: 'class-waitlists',
        question: {
          en: 'Are there waitlists for full classes?',
          ar: 'هل توجد قوائم انتظار للحصص الممتلئة؟',
        },
        answer: {
          en: 'Yes. When a class fills up, customers join a waitlist and get notified automatically when a spot opens, so cancellations turn into filled seats instead of lost revenue.',
          ar: 'نعم. عندما تمتلئ الحصة ينضم العملاء لقائمة الانتظار ويصلهم إشعار تلقائي فور توفر مقعد، فتتحول الإلغاءات إلى مقاعد مشغولة بدل إيراد ضائع.',
        },
      },
      {
        id: 'access-control',
        question: {
          en: 'Does Flow handle access control, check-in and guest passes?',
          ar: 'هل تدير Flow التحكم بالدخول وتسجيل الحضور وتصاريح الضيوف؟',
        },
        answer: {
          en: 'Yes. Gate scans and member check-in verify who is walking in, and guest passes let members invite guests from the app while the front desk sees who is expected. Everything is logged, so you know exactly who used the facility and when.',
          ar: 'نعم. مسح البوابة وتسجيل دخول الأعضاء يتحققان ممن يدخل المنشأة، وتصاريح الضيوف تتيح للأعضاء دعوة ضيوفهم من التطبيق بينما يعرف الاستقبال من سيحضر. وكل ذلك مسجّل، فتعرف بدقة من استخدم المنشأة ومتى.',
        },
      },
    ],
  },
];

export function allFaqEntries(): FaqEntry[] {
  return FAQ_GROUPS.flatMap((group) => group.entries);
}

export function featuredFaqEntries(): FaqEntry[] {
  return allFaqEntries().filter((entry) => entry.featured);
}

export function localizeFaqEntries(
  entries: FaqEntry[],
  locale: MarketingLocale,
): { question: string; answer: string }[] {
  return entries.map((entry) => ({
    question: entry.question[locale],
    answer: entry.answer[locale],
  }));
}
